import { useCallback } from 'react';
import { CallInsightApi, useCallInsightController } from '@frontend/call-insight-panel';
import { GlobalPanel } from '@frontend/panel-engine';
import { useQueueStore } from '@frontend/queue';

/**
 * Call-history-scoped wrapper around the call-insight controller.
 *
 * Enforces mutual exclusivity with the voicemail-details panel: opening the
 * call-insight panel first closes voicemail-details, so the two panels never
 * appear at the same time (they replace each other rather than stacking).
 *
 * The voicemail panel is hidden synchronously (in the same click handler, before
 * the next paint) so the panels never briefly stack, and a close is enqueued so
 * its store/state is torn down properly.
 *
 * Only these two panels are affected; all other global panels keep stacking. The
 * complementary direction (opening voicemail closes call-insight) is handled at
 * the voicemail open site via `closeCallInsightPanel`.
 */
export const useCallHistoryCallInsight = (): CallInsightApi => {
  const controller = useCallInsightController({ source: 'callHistory' });
  const { open: openCallInsight } = controller;

  const open = useCallback<CallInsightApi['open']>(
    (params, opts) => {
      if (GlobalPanel.isOpen('voicemail-details')) {
        GlobalPanel.close('voicemail-details');
        useQueueStore.getState().enqueue('voicemail-details', { payload: { type: 'close' } });
      }
      openCallInsight(params, opts);
    },
    [openCallInsight]
  );

  return { ...controller, open };
};
